<template>
  <Card v-if="pageErrors.length > 0">
    <CardBody>
      <div
        v-for="(err, index) in pageErrors"
        :key="index"
      >
        {{ err.message }}
      </div>
    </CardBody>
  </Card>
  <div v-else-if="agreementIsLoading">
    <Skeleton
      width="100%"
      height="6rem"
      class="mb-4"
    />
    <div class="grid lg:grid-cols-4 gap-4">
      <Skeleton
        width="100%"
        height="50vh"
      />
      <div class="col-span-3">
        <Skeleton
          width="100%"
          height="4rem"
        />
        <div class="flex">
          <Skeleton
            width="50%"
            height="60vh"
            class="mt-6"
          />
          <Skeleton
            width="50%"
            height="60vh"
            class="ml-6 mt-6"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-else-if="agreementData"
    :key="route.params.agid"
  >
    <AgreementHeader
      :agreement-data="agreementData"
      :tags-to-display="tagsToDisplay"
      :reminders-counter="remindersCounter"
      @status-changed="handleStatusChanged"
      @remove-tag="handleRemoveTag"
      @open-tags-modal="tagsModalIsOpen = true"
      @open-reminders-modal="remindersIsOpen = true"
      @toggle-archive="handleToggleArchive"
      @open-merge-modal="mergeModalIsOpen = true"
      @open-transfer-modal="transferModalIsOpen = true"
      @delete-agreement="handleDeleteAgreement"
    />

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <AgreementCustomerPanel
        :agreement-data="agreementData"
        :external-systems="externalSystems"
        :route-coordinates="routeCoordinates"
        @open-contact-preferences="contactPreferencesIsOpen = true"
        @new-conversation="handleNewConversationTrigger"
        @update:route-coordinates="handleRouteCoordinatesUpdate"
      />

      <div class="lg:col-span-3 ml-6">
        <!-- Menu -->
        <div class="-mt-2">
          <Tabs
            v-model="currentTab"
            :tabs="tabs"
            :direction="useBreakPoints({ xs: 'vertical', sm: 'horizontal' })"
            type="underlined"
          />
        </div>

        <!-- Overview -->
        <AgreementOverviewTab
          v-show="currentTab === 'overview'"
          :agreement-data="agreementData"
          :agreement-i-d="agreementID"
          :recent-timeline-data="recentTimelineData"
          :appointments-data="appointmentsData"
          :show-full-details="agreementFullDetailsIsOpen"
          @open-update-mileage-modal="openUpdateMileageModal"
          @toggle-full-details="agreementFullDetailsIsOpen = !agreementFullDetailsIsOpen"
          @note-added="handleNoteAdded"
          @edit-note="handleEditNote"
          @update:recent-timeline-data="handleRecentTimelineUpdate"
        />

        <!-- Activity Timeline -->
        <AgreementActivityTab
          v-show="currentTab === 'activity'"
          :agreement-i-d="agreementID"
          :timeline-data="timelineData"
          @note-added="handleNoteAdded"
          @edit-note="handleEditNote"
          @update:timeline-data="handleTimelineUpdate"
        />

        <!-- Conversations -->
        <AgreementConversationsTab
          v-show="currentTab === 'conversations'"
          ref="conversationsTabRef"
          :agreement-data="agreementData"
          :conversations-data="conversationsData"
          @new-conversation-submitted="handleNewConversationSubmitted"
        />

        <!-- Appointments -->
        <AgreementAppointmentsTab
          v-show="currentTab === 'appointments'"
          :agreement-data="agreementData"
          :appointments-data="appointmentsData"
          :appointment-i-d="route.params.id || null"
          @new-appointment="handleNewAppointmentClick"
          @send-invite="handleAppointmentInviteClick"
          @appointment-updated="handleAppointmentUpdated"
        />

        <!-- Vehicle -->
        <AgreementVehicleTab
          v-show="currentTab === 'vehicle'"
          :agreement-data="agreementData"
          @valuation-submitted="handleValuationSubmitted"
        />

        <!-- Switch -->
        <AgreementSwitchTab
          v-show="currentTab === 'switch'"
          :new-vehicles="switchExplore.vehicles.new"
          :used-vehicles="switchExplore.vehicles.used"
          :favourites="switchExplore.favourites"
          @open-proposal-builder="proposalBuilderIsOpen = true"
          @update:new-vehicles="switchExplore.vehicles.new = $event"
          @update:used-vehicles="switchExplore.vehicles.used = $event"
          @update:favourites="switchExplore.favourites = $event"
        />
      </div>
    </div>
  </div>
  <Empty
    v-else
    title="No agreement found"
  />

  <teleport to="#modals-container">
    <!-- Contact Preferences Modal -->
    <AgreementContactPreferencesModal
      :is-open="contactPreferencesIsOpen"
      :contact-permissions="agreementData?.contactPermissions"
      @close="contactPreferencesIsOpen = false"
      @save="handleContactPreferencesSave"
    />

    <!-- Reminders Modal -->
    <AgreementRemindersModal
      :is-open="remindersIsOpen"
      :reminders="computedReminders"
      @close="remindersIsOpen = false"
      @update-task="handleTaskListItemChange"
      @task-clicked="handleReminderClick"
      @new-reminder="handleNewReminderClick"
    />

    <!-- Tags Modal -->
    <AgreementTagsModal
      :is-open="tagsModalIsOpen"
      :selected-tag-i-ds="agreementData?.tagIDs || []"
      :all-tags-list="allTagsList"
      @close="tagsModalIsOpen = false"
      @add-tag="handleAddTag"
      @remove-tag="handleRemoveTag"
    />

    <!-- Merge Modal -->
    <AgreementMergeModal
      :is-open="mergeModalIsOpen"
      :is-loading="mergeAgreement.isLoading.value"
      :from-agreement-i-d="parseInt(String(agreementID))"
      :selected-agreement-i-d="mergeAgreementData.to"
      :search-term="searchTerm"
      :agreements-list="agreementsList"
      :has-error="mergeV$.to.$error"
      :error-message="mergeV$.to.$errors[0]?.$message || null"
      @close="handleMergeModalClose"
      @save="submitMergeAgreement"
      @update:selected-agreement-id="mergeAgreementData.to = $event"
      @update:search-term="searchTerm = $event"
    />

    <!-- Transfer Modal -->
    <AgreementTransferModal
      :is-open="transferModalIsOpen"
      :is-loading="transferAgreement.isLoading.value"
      :selected-dealership-i-d="transferAgreementData.to"
      :dealerships-list="dealershipsList"
      :note="transferAgreementData.note"
      :dealership-has-error="transferV$.to.$error"
      :dealership-error-message="transferV$.to.$errors[0]?.$message || null"
      :note-has-error="transferV$.note.$error"
      :note-error-message="transferV$.note.$errors[0]?.$message || null"
      :user-profile-pic-url="loggedInUserProfilePicUrl"
      :user-initials="authStore.details.initials"
      :user-name="authStore.details.name"
      @close="handleTransferModalClose"
      @save="submitTransferAgreement"
      @update:selected-dealership-id="transferAgreementData.to = $event"
      @update:note="transferAgreementData.note = $event"
    />

    <!-- Edit Note Modal -->
    <AgreementEditNoteModal
      :is-open="editNoteModalIsOpen"
      :is-loading="editNoteApiRequest.isLoading.value"
      :has-unsaved-changes="editNoteHasUnsavedChanges"
      :content="editNoteState.content"
      :has-error="editNoteV$.content.$error"
      :error-message="editNoteV$.content.$errors[0]?.$message || null"
      @close="editNoteModalIsOpen = false"
      @save="handleSubmitEditNote"
      @update:content="editNoteState.content = $event"
    />

    <!-- Invite Modal -->
    <AgreementInviteModal
      :is-open="inviteTemplatesModalIsOpen"
      :is-loading="appointmentInviteApiRequest.isLoading.value"
      :has-unsaved-changes="false"
      :customer-name="agreementData?.customer?.name || ''"
      :selected-template-i-d="selectedTemplate.id"
      :selected-template-subject="selectedTemplate.subject"
      :selected-template-body="selectedTemplate.body"
      :marketing-templates="marketingTemplates"
      :has-error="inviteV$.id.$error"
      :error-message="inviteV$.id.$errors[0]?.$message || null"
      @close="closeInviteModal"
      @save="sendAppointmentInvite"
      @update:selected-template-id="selectedTemplate.id = $event"
      @select-template="selectTemplate"
    />

    <!-- Update Mileage Modal -->
    <AgreementUpdateMileageModal
      :is-open="updateMileageModalIsOpen"
      :is-loading="updateMileageApiRequest.isLoading.value"
      :current-mileage="agreementData?.odometer?.current || null"
      :unit="agreementData?.odometer?.unit || null"
      :mileage="updateMileageState.mileage"
      :has-error="updateMileageV$.mileage?.$error"
      :error-message="updateMileageV$.mileage?.$errors[0]?.$message || null"
      @close="handleUpdateMileageModalClose"
      @save="submitUpdateMileage"
      @update:mileage="updateMileageState.mileage = $event"
    />

    <!-- Task Handler -->
    <TaskHandler
      :key="randomKey"
      :is-open="taskHandlerIsOpen"
      :task="taskHandlerData"
      class="!z-[121]"
      @close-task-handler="taskHandlerIsOpen = false"
      @new-task-submit="newReminderSubmit"
      @update-task-submit="updateReminderSubmit"
    />

    <!-- Appointment Handler -->
    <AppointmentHandler
      v-if="appointmentHandlerData"
      :key="randomKey"
      v-model:is-open="appointmentHandlerIsOpen"
      :agreement-contact-details="agreementData.customer.contact"
      :appointment="appointmentHandlerData"
      :dealership-manufacturer-name="agreementData.dealership.manufacturerName"
      :agreement-vehicle-make="agreementData.vehicle?.make"
      :dealership-automated-comms-config="agreementData.dealership.automatedCommsConfig"
      @close-appointment-handler="appointmentHandlerIsOpen = false"
      @new-appointment-submit="newAppointmentSubmitted"
    />

    <!-- Proposal Builder -->
    <ProposalBuilder
      :key="randomKey"
      :proposal="proposalData"
      :is-open="proposalBuilderIsOpen"
      @close-handler="proposalBuilderIsOpen = false"
    />
  </teleport>
</template>

<script setup lang="ts">
// composables
import { ref, computed, watch, inject, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useTaskStore } from '@/stores/TaskStore'
import { useCallCenterStore } from '@/stores/CallCenterStore'
// composables - resources
import useAgreementData from '@/composables/resources/useAgreementData'
import useAgreementTimezone from '@/composables/resources/useAgreementTimezone'
import useAgreementStatus from '@/composables/resources/useAgreementStatus'
import useAgreementTags from '@/composables/resources/useAgreementTags'
import useAgreementReminders from '@/composables/resources/useAgreementReminders'
import useAgreementMileage from '@/composables/resources/useAgreementMileage'
import useAgreementTabs from '@/composables/resources/useAgreementTabs'
import useAgreementAppointments from '@/composables/resources/useAgreementAppointments'
import useAgreementConversations from '@/composables/resources/useAgreementConversations'
import useAgreementTimeline from '@/composables/resources/useAgreementTimeline'
import useAgreementContact from '@/composables/resources/useAgreementContact'
import useAgreementMerge from '@/composables/resources/useAgreementMerge'
import useAgreementTransfer from '@/composables/resources/useAgreementTransfer'
import useAgreementPlaceholders from '@/composables/resources/useAgreementPlaceholders'
import useAgreementExternalSystems from '@/composables/resources/useAgreementExternalSystems'
// composables
import useApiRequest from '@/composables/useApiRequest'
import { useBreakPoints } from '@/composables/useWindowSize'
// components
import Tabs from '@/components/tabs/Tabs.vue'
import Empty from '@/components/empty/Empty.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import TaskHandler from '@/components/tasks/TaskHandler.vue'
import AppointmentHandler from '@/components/unique/appointments/AppointmentHandler.vue'
import ProposalBuilder from '@/components/unique/switch/ProposalBuilder.vue'
// components - agreement
import AgreementHeader from '@/components/unique/agreement/AgreementHeader.vue'
import AgreementCustomerPanel from '@/components/unique/agreement/AgreementCustomerPanel.vue'
import AgreementOverviewTab from '@/components/unique/agreement/tabs/AgreementOverviewTab.vue'
import AgreementActivityTab from '@/components/unique/agreement/tabs/AgreementActivityTab.vue'
import AgreementConversationsTab from '@/components/unique/agreement/tabs/AgreementConversationsTab.vue'
import AgreementAppointmentsTab from '@/components/unique/agreement/tabs/AgreementAppointmentsTab.vue'
import AgreementVehicleTab from '@/components/unique/agreement/tabs/AgreementVehicleTab.vue'
import AgreementSwitchTab from '@/components/unique/agreement/tabs/AgreementSwitchTab.vue'
// components - modals
import AgreementContactPreferencesModal from '@/components/unique/agreement/modals/AgreementContactPreferencesModal.vue'
import AgreementRemindersModal from '@/components/unique/agreement/modals/AgreementRemindersModal.vue'
import AgreementTagsModal from '@/components/unique/agreement/modals/AgreementTagsModal.vue'
import AgreementMergeModal from '@/components/unique/agreement/modals/AgreementMergeModal.vue'
import AgreementTransferModal from '@/components/unique/agreement/modals/AgreementTransferModal.vue'
import AgreementEditNoteModal from '@/components/unique/agreement/modals/AgreementEditNoteModal.vue'
import AgreementInviteModal from '@/components/unique/agreement/modals/AgreementInviteModal.vue'
import AgreementUpdateMileageModal from '@/components/unique/agreement/modals/AgreementUpdateMileageModal.vue'
// utilities
import { serializeEmailBodyForDelivery } from '@/utilities/communications/templateEditor'
import { alertToast } from '@/utilities/notification'
import Swal from 'sweetalert2'
import { StockService } from '@/components/unique/switch/TriumphStock'
import { NewStockService } from '@/components/unique/switch/TriumphStockNew'

const route = useRoute()
const router = useRouter()
const emitter = inject('emitter')
const authStore = useAuthStore()
const taskStore = useTaskStore()
const callCenterStore = useCallCenterStore()

// Agreement ID from route
const agreementID = ref<string | number>(route.params.agid as string)

// Initialize composables
const { agreementData, pageErrors, dealershipIDs, agreementIsLoading, fetchAgreementData, updateAgreementData } =
  useAgreementData(agreementID)

const { routeCoordinates, customerTimeZone, setCustomerTimeZone } = useAgreementTimezone(
  agreementID,
  updateAgreementData
)

const { statusChanged: handleStatusChanged } = useAgreementStatus(agreementData, updateAgreementData)

const {
  allTagsList,
  getTagsToDisplay,
  fetchAllTags,
  removeTag: removeTagFromAgreement,
  addTag: addTagToAgreement
} = useAgreementTags(agreementID)

const { computedReminders, remindersCounter } = useAgreementReminders(agreementID)

const {
  updateMileageModalIsOpen,
  updateMileageState,
  updateMileageV$,
  updateMileageApiRequest,
  openUpdateMileageModal,
  submitUpdateMileage
} = useAgreementMileage(agreementID, agreementData)

const {
  appointmentsCounter,
  appointmentsData,
  appointmentHandlerIsOpen,
  appointmentHandlerData,
  randomKey,
  inviteTemplatesModalIsOpen,
  appointmentInviteApiRequest,
  fetchAppointments,
  handleNewAppointmentClick: handleNewAppointmentClickComposable,
  handleAppointmentInviteClick: handleAppointmentInviteClickComposable,
  openAppointmentHandler,
  onAppointmentUpdated,
  newAppointmentSubmitted
} = useAgreementAppointments(agreementID, agreementData)

const { currentTab, tabs } = useAgreementTabs(agreementID, appointmentsCounter)

const {
  conversationsData,
  conversationsTabRef,
  fetchConversations,
  newConversationSubmitted: handleNewConversationSubmitted,
  scrollConversationsToBottom
} = useAgreementConversations(agreementID)

const {
  timelineData,
  recentTimelineData,
  editNoteModalIsOpen,
  editNoteHasUnsavedChanges,
  editNoteState,
  editNoteV$,
  editNoteApiRequest,
  fetchTimeline,
  onNoteAdded: handleNoteAdded,
  editNote: handleEditNote,
  submitEditNote
} = useAgreementTimeline(agreementID)

const {
  contactPreferencesIsOpen,
  validatePhoneNumbers,
  submitContactPreferences: submitContactPreferencesComposable
} = useAgreementContact(agreementData, updateAgreementData)

const {
  mergeModalIsOpen,
  mergeAgreementData,
  searchTerm,
  agreementsList,
  mergeV$,
  mergeAgreement,
  submitMergeAgreement
} = useAgreementMerge(agreementID, dealershipIDs)

const {
  transferModalIsOpen,
  transferAgreementData,
  dealershipsList,
  transferV$,
  transferAgreement,
  fetchDealerships,
  submitTransferAgreement
} = useAgreementTransfer(agreementID, agreementData)

const { externalSystems, fetchExternalSystemLogins } = useAgreementExternalSystems(agreementData)

// Additional state
const tagsModalIsOpen = ref(false)
const remindersIsOpen = ref(false)
const taskHandlerIsOpen = ref(false)
const taskHandlerData = ref(null)
const agreementFullDetailsIsOpen = ref(false)
const proposalBuilderIsOpen = ref(false)
const proposalData = ref(null)
const loggedInUserProfilePicUrl = authStore.details.profilePicUrl

// Computed selectedCallCenterCommsConfig
const selectedCallCenterCommsConfig = computed(() => {
  if (!agreementData.value || !callCenterStore.selectedCallCenter) {
    return null
  }
  return (
    agreementData.value.dealership.commsConfig.find(
      config => config.callCenterId.toString() === callCenterStore.selectedCallCenter
    ) || null
  )
})

// Placeholders composable (needs selectedCallCenterCommsConfig)
const { initPlaceholders, replacePlaceholders } = useAgreementPlaceholders(
  agreementData,
  selectedCallCenterCommsConfig
)

/**
 * Initializes invite/conversation placeholders when agreement or comms config changes.
 * This prevents stale placeholders during in-app navigation before comms config resolves.
 */
function syncAgreementPlaceholders(): void {
  initPlaceholders()
}

// Switch explore data
const switchExplore = ref({
  vehicles: {
    new: [],
    used: []
  },
  favourites: [2]
})

// Marketing templates for invites
const marketingTemplates = ref<Array<{ id: number; name: string; subject: string; body: string }> | null>(null)
const selectedTemplate = ref({
  id: null as number | null,
  subject: null as string | null,
  body: null as string | null
})

// Invite validation
const inviteRules = {
  id: { required }
}
const inviteV$ = useVuelidate(inviteRules, selectedTemplate)

// Tags to display computed
const tagsToDisplay = computed(() => {
  if (!agreementData.value) return []
  return getTagsToDisplay(agreementData.value)
})

// Watch for tab changes to scroll conversations
watch(
  () => currentTab.value,
  async newTab => {
    if (newTab === 'conversations') {
      await nextTick()
      // Call the exposed method from the component if available
      const component = conversationsTabRef.value as { scrollToBottom?: () => void } | null
      if (component && typeof component.scrollToBottom === 'function') {
        component.scrollToBottom()
      } else {
        // Fallback to the composable method
        scrollConversationsToBottom()
      }
    }
  }
)

// Watch for customer timezone changes
watch(customerTimeZone, () => {
  if (appointmentHandlerData.value) {
    appointmentHandlerData.value.customerTimeZone = customerTimeZone.value
  }
})

watch([agreementData, selectedCallCenterCommsConfig], () => {
  syncAgreementPlaceholders()
})

// Watch for route param changes
watch(
  () => route.params.agid,
  (newAGID, oldAGID) => {
    if (oldAGID != undefined && newAGID != undefined && newAGID !== oldAGID) {
      agreementID.value = newAGID as string
      pageErrors.value = []
      emitter.emit('phone-clear-numbers')
      fetchAllData()
    }
    if (newAGID === undefined) {
      emitter.emit('phone-clear-numbers')
    }
  },
  { immediate: true }
)

/**
 * Fetches all data needed for the agreement view
 */
async function fetchAllData() {
  await fetchAgreementData()

  if (!agreementData.value || pageErrors.value.length > 0) {
    return
  }

  // Set timezone if available
  if (agreementData.value.timeZone) {
    setCustomerTimeZone(agreementData.value.timeZone)
  }

  // Initialize appointment handler data
  appointmentHandlerData.value = {
    agreementID: agreementID.value,
    dealershipID: agreementData.value.dealership.id,
    timeZone: agreementData.value.dealership.timeZone,
    customerTimeZone: customerTimeZone.value,
    testDriveVehicleMake: agreementData.value.dealership.manufacturerName ?? null
  }

  // Validate phone numbers
  validatePhoneNumbers()

  // Fetch related data
  fetchTimeline()
  fetchAppointments()
  fetchConversations()
  taskStore.fetchTasks()
  fetchAllTags()
  fetchExternalSystemLogins()
  // Note: fetchDealerships() is now called automatically when transfer modal opens

  // Load switch explore data
  StockService.getStock().then(data => (switchExplore.value.vehicles.used = data))
  NewStockService.getModels().then(data => (switchExplore.value.vehicles.new = data))

  // Initialize placeholders
  syncAgreementPlaceholders()
}

/**
 * Handles route coordinates update
 */
function handleRouteCoordinatesUpdate(coordinates: Record<string, unknown>) {
  routeCoordinates.value = coordinates
}

/**
 * Handles new conversation trigger
 */
function newConversationTrigger(commType: string, contactTo: string) {
  if (commType == null) {
    return
  }

  if (commType == 'call') {
    loadPhone(contactTo)
  }
  if (commType == 'sms') {
    router.replace('/agreement/' + agreementID.value + '/conversations').then(() => {
      nextTick(() => {
        emitter.emit('agreement-new-conversation', 'sms')
      })
    })
  }
  if (commType == 'email') {
    router.replace('/agreement/' + agreementID.value + '/conversations').then(() => {
      nextTick(() => {
        emitter.emit('agreement-new-conversation', 'email')
      })
    })
  }
}

function handleNewConversationTrigger(type: string, value: string) {
  newConversationTrigger(type, value)
}

/**
 * Loads phone numbers for calling
 */
function loadPhone(numberTo: string) {
  if (selectedCallCenterCommsConfig.value == null) {
    alertToast('Failed to load phone', 'No call center selected', 'danger')
    return
  }

  emitter.emit('phone-set-numbers', {
    from: {
      number: selectedCallCenterCommsConfig.value.phoneNumber,
      name: selectedCallCenterCommsConfig.value.phoneNumberName
    },
    to: {
      number: numberTo,
      agreementId: agreementID.value,
      name: agreementData.value?.customer.name
    }
  })
}

/**
 * Handles contact preferences save
 */
async function handleContactPreferencesSave(permissions: { canCall: boolean; canEmail: boolean; canSms: boolean }) {
  if (!agreementData.value) return

  agreementData.value.contactPermissions.canCall = permissions.canCall
  agreementData.value.contactPermissions.canEmail = permissions.canEmail
  agreementData.value.contactPermissions.canSms = permissions.canSms

  await submitContactPreferencesComposable()
  contactPreferencesIsOpen.value = false
}

/**
 * Handles tag removal
 */
function handleRemoveTag(tagID: number) {
  if (!agreementData.value) return
  removeTagFromAgreement(tagID, agreementData)
}

/**
 * Handles tag addition
 */
function handleAddTag(tagID: number) {
  addTagToAgreement(tagID)
  if (agreementData.value && !agreementData.value.tagIDs.includes(tagID)) {
    agreementData.value.tagIDs.push(tagID)
  }
}

/**
 * Handles toggle archive
 */
function handleToggleArchive() {
  if (!agreementData.value) return
  updateAgreementData({ isArchived: !agreementData.value.markers.archived })
  agreementData.value.markers.archived = !agreementData.value.markers.archived
}

/**
 * Handles delete agreement
 */
function submitDeleteAgreement() {
  const deleteAgreement = useApiRequest()
  Swal.fire({
    title: 'Are you sure?',
    text: 'This cannot be reversed. Please triple check before deleting.',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Confirm Deletion',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel',
    cancelButtonColor: 'var(--bg-secondary)'
  }).then(function (response) {
    if (response.isConfirmed) {
      deleteAgreement.send({ endpoint: '/v1/agreements/' + agreementID.value, method: 'DELETE' }).then(() => {
        if (!deleteAgreement.hasError) {
          alertToast('Deleted', 'Agreement #' + agreementID.value + ' was deleted', 'success')
          router.replace('/')
        } else {
          alertToast('Failed to delete agreement #' + agreementID.value, '', 'error')
        }
      })
    }
  })
}

function handleDeleteAgreement() {
  submitDeleteAgreement()
}

/**
 * Handles merge modal close
 */
function handleMergeModalClose() {
  mergeModalIsOpen.value = false
  mergeAgreementData.to = null
}

/**
 * Handles transfer modal close
 */
function handleTransferModalClose() {
  transferModalIsOpen.value = false
  transferAgreementData.to = null
  transferAgreementData.note = null
}

/**
 * Handles update mileage modal close
 */
function handleUpdateMileageModalClose() {
  updateMileageModalIsOpen.value = false
  updateMileageState.mileage = null
  if (updateMileageV$.value) {
    updateMileageV$.value.$reset()
  }
}

/**
 * Handles new appointment click
 */
function handleNewAppointmentClick() {
  handleNewAppointmentClickComposable(openAppointmentHandler)
}

/**
 * Handles appointment invite click
 */
function handleAppointmentInviteClick() {
  handleAppointmentInviteClickComposable(() => {
    inviteTemplatesModalIsOpen.value = true
    fetchMarketingTemplates()
  })
}

/**
 * Handles reminder click
 */
function handleReminderClick(task: unknown) {
  randomKey.value = Math.random()
  taskHandlerData.value = task
  taskHandlerIsOpen.value = true
}

/**
 * Handles new reminder click
 */
function handleNewReminderClick() {
  randomKey.value = Math.random()
  taskHandlerData.value = {
    linkType: 'agreement',
    linkTitle: agreementData.value?.customer.name,
    linkId: agreementID.value,
    isReminder: true
  }
  taskHandlerIsOpen.value = true
}

/**
 * Handles new reminder submit
 */
function newReminderSubmit() {
  taskHandlerIsOpen.value = false
}

/**
 * Handles update reminder submit
 */
function updateReminderSubmit() {
  taskHandlerIsOpen.value = false
}

/**
 * Handles task list item change
 */
function handleTaskListItemChange(task: unknown) {
  taskStore.updateTaskLocal(task as { id: number | string; [key: string]: unknown })
}

/**
 * Handles valuation submitted
 */
function onNewValuationSubmit(data: { latestMileage: number }) {
  if (agreementData.value?.odometer) {
    agreementData.value.odometer.current = data.latestMileage
  }
}

function handleValuationSubmitted(data: Record<string, unknown>) {
  onNewValuationSubmit(data as { latestMileage: number })
}

/**
 * Handles appointment updated
 */
function handleAppointmentUpdated(data: Record<string, unknown>) {
  onAppointmentUpdated(data as { id: number })
}

/**
 * Handles recent timeline data update
 */
function handleRecentTimelineUpdate(items: Array<Record<string, unknown>>) {
  recentTimelineData.value = items
}

/**
 * Handles timeline data update
 */
function handleTimelineUpdate(items: Array<Record<string, unknown>>) {
  timelineData.value = items
}

/**
 * Handles edit note save
 */
async function handleSubmitEditNote(): Promise<void> {
  await submitEditNote()
}

/**
 * Fetches marketing templates for appointment invites
 */
function fetchMarketingTemplates() {
  useApiRequest()
    .send({
      endpoint: '/v1/comms/templates',
      method: 'GET',
      params: {
        dealershipID: agreementData.value?.dealership.id,
        types: 'email',
        usedFor: 'marketing'
      }
    })
    .then(response => {
      if (!response.success) {
        alertToast('Failed to fetch invite templates', response.message, 'error')
      }
      marketingTemplates.value = response.data
    })
    .catch(err => {
      alertToast('Failed to fetch invite templates', (err as Error).message, 'error')
    })
}

/**
 * Selects a template for appointment invite
 */
function selectTemplate(selected: { id: number }) {
  const template = marketingTemplates.value?.find((t: { id: number }) => t.id === selected.id)

  if (template) {
    const subject = template.subject ? replacePlaceholders(template.subject) : null
    const body = template.body ? replacePlaceholders(template.body) : null

    selectedTemplate.value = {
      id: template.id,
      subject,
      body: serializeEmailBodyForDelivery(body)
    }
  }
}

/**
 * Closes invite modal
 */
function closeInviteModal() {
  inviteTemplatesModalIsOpen.value = false
  selectedTemplate.value = {
    id: null,
    subject: null,
    body: null
  }
}

/**
 * Sends appointment invite
 */
async function sendAppointmentInvite() {
  if (!selectedTemplate.value.id) {
    return
  }

  const response = await appointmentInviteApiRequest.send({
    endpoint: '/v1/appointment-invites',
    method: 'POST',
    data: JSON.stringify({
      agreementId: agreementID.value,
      emailSubject: selectedTemplate.value.subject,
      emailBody: selectedTemplate.value.body
    })
  })

  if (!response.success) {
    alertToast('Failed to send invite', response.message, 'error')
    return
  }

  alertToast('Appointment Invite Sent', null, 'success')
  closeInviteModal()
}

// Initialize on mount
onMounted(() => {
  if (agreementID.value == null || agreementID.value == 0) {
    pageErrors.value.push({ message: 'AgreementID missing or Malformed' })
  } else {
    fetchAllData()
  }
})
</script>
